// frontend/src/pages/ProcurementUploadPage.tsx
// v4.0 - OMEGA PROCUREMENT (Persistent State & Slim UI)

import { useState, FC } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { AxiosError } from 'axios';
import {
    Box, Button, Container, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, 
    Stack, Alert, CircularProgress, styled, Chip, Checkbox, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    RadioGroup, FormControlLabel, Radio, Autocomplete, IconButton, Tooltip, AlertTitle
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import axiosInstance from '../config/axiosInstance';
import { PROCUREMENT_FIELDS } from '../config/procurementFields';

// --- ESTILOS ---
const cardBaseSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    width: '100%',
    boxShadow: 'none',
    mb: 3,
    overflow: 'hidden' // Importante para la barra de carga
};

const uploadZoneSx = {
    border: '1px dashed #334155',
    borderRadius: 2,
    p: 3,
    textAlign: 'center',
    cursor: 'pointer',
    bgcolor: 'rgba(255, 255, 255, 0.01)',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    minHeight: '100px',
    '&:hover': {
        bgcolor: 'rgba(56, 189, 248, 0.05)',
        borderColor: '#38bdf8'
    }
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', height: 1, overflow: 'hidden',
    position: 'absolute', bottom: 0, left: 0, whiteSpace: 'nowrap', width: 1,
});

// --- TIPOS ---
interface ProcurementStatus {
    has_data: boolean;
    total_records: number;
    last_upload_date: string | null;
    min_date: string | null; // NUEVO
    max_date: string | null; // NUEVO
}

interface ParetoItem { sku: string; total_spend: number; frequency: number; avg_price: number; currency: string; }
interface Family { id: string; name: string; linked_ticker?: string; }
interface UploadResponse { filename: string; gcs_object_name: string; headers: string[]; preview: Record<string, string>; }

interface UploadState {
    file: File | null; gcsObjectName: string | null; uploading: boolean; processing: boolean;
    error: string | null; userHeaders: string[]; previewData: Record<string, string>; mapping: Record<string, string>;
}

const initialState: UploadState = {
    file: null, gcsObjectName: null, uploading: false, processing: false,
    error: null, userHeaders: [], previewData: {}, mapping: {}
};

const ProcurementUploadPage: FC = () => {
    const [state, setState] = useState<UploadState>(initialState);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    // --- STATE UI ---
    const [showOverride, setShowOverride] = useState(false); // Controla si mostramos Dropzone aunque haya datos
    const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [assignMode, setAssignMode] = useState<'existing' | 'new'>('existing');
    const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
    const [newFamilyName, setNewFamilyName] = useState('');

    const updateState = (updates: Partial<UploadState>) => setState(prev => ({ ...prev, ...updates }));

    // 0. QUERY: STATUS (PERSISTENCIA)
    const { data: statusData, isLoading: loadingStatus } = useQuery({
        queryKey: ['procurementStatus'],
        queryFn: async () => {
            const res = await axiosInstance.get<ProcurementStatus>('/procurement/status');
            return res.data;
        }
    });

    // 1. QUERY: FAMILIAS
    const { data: families = [] } = useQuery({
        queryKey: ['families'],
        queryFn: async () => (await axiosInstance.get<Family[]>('/procurement/families')).data
    });

    // 2. QUERY: PARETO
    const { data: paretoData = [], isLoading: loadingPareto, refetch: refetchPareto } = useQuery({
        queryKey: ['pareto'],
        queryFn: async () => (await axiosInstance.get<ParetoItem[]>('/procurement/dashboard/pareto?limit=50')).data
    });

    // 3. MUTATIONS (Upload & Process)
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData(); formData.append('file', file);
            return (await axiosInstance.post<UploadResponse>('/procurement/upload', formData)).data;
        },
        onSuccess: (data, file) => {
            const autoMap: Record<string, string> = {};
            PROCUREMENT_FIELDS.forEach(field => {
                const match = data.headers.find(h => h.toLowerCase().includes(field.label.toLowerCase()) || h.toLowerCase() === field.key);
                if (match) autoMap[field.key] = match;
            });
            updateState({ uploading: false, file, gcsObjectName: data.gcs_object_name, userHeaders: data.headers, previewData: data.preview, mapping: autoMap });
        },
        onError: (err: AxiosError<{ detail: string }>) => {
            updateState({ uploading: false, error: err.response?.data?.detail || "Error subiendo archivo" });
        }
    });

    const processMutation = useMutation({
        mutationFn: async () => {
            if (!state.gcsObjectName) throw new Error("Falta referencia GCS");
            await axiosInstance.post('/procurement/process', { gcs_object_name: state.gcsObjectName, mapping: state.mapping });
        },
        onSuccess: () => {
            enqueueSnackbar('Datos procesados correctamente.', { variant: 'success' });
            updateState({ processing: false });
            setShowOverride(false); // Volver a vista compacta
            // Resetear formulario interno tras éxito
            setTimeout(() => updateState(initialState), 500);
            queryClient.invalidateQueries({ queryKey: ['procurementStatus'] });
            queryClient.invalidateQueries({ queryKey: ['pareto'] });
        },
        onError: (err: AxiosError<{ detail: string }>) => updateState({ processing: false, error: err.response?.data?.detail || "Error procesando" })
    });

    // 4. MUTATION: ASSIGN
    const assignMutation = useMutation({
        mutationFn: async () => {
            let targetId = selectedFamilyId;
            if (assignMode === 'new') {
                targetId = (await axiosInstance.post<Family>('/procurement/families', { name: newFamilyName })).data.id;
            }
            if (!targetId) throw new Error("Familia inválida");
            await axiosInstance.post('/procurement/skus/assign', { family_id: targetId, skus: selectedSkus });
        },
        onSuccess: () => {
            enqueueSnackbar('Asignación completada', { variant: 'success' });
            setModalOpen(false); setSelectedSkus([]); setNewFamilyName('');
            queryClient.invalidateQueries({ queryKey: ['families'] });
            queryClient.invalidateQueries({ queryKey: ['pareto'] });
        },
        onError: (err: any) => enqueueSnackbar(err.response?.data?.detail || "Error", { variant: 'error' })
    });

    // --- LOGIC: SHOW DROPZONE? ---
    // Mostramos Dropzone SI: (No hay datos cargados AÚN) O (El usuario pulsó "Actualizar Datos") O (Estamos en proceso de carga/mapeo)
    const hasData = statusData?.has_data;
    const isMapping = state.userHeaders.length > 0;
    const showDropzone = !hasData || showOverride || isMapping;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            
            {/* HEADER */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                    Auditoría de Compras
                </Typography>
                <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                    Gestión de ingesta y clasificación estratégica de gasto.
                </Typography>
            </Box>

            {/* MÓDULO 1: ESTADO DE DATOS (SLIM CARD vs DROPZONE) */}
            <Paper elevation={0} sx={{ ...cardBaseSx, p: showDropzone ? 3 : 0 }}> 
                {/* Si mostramos Dropzone, padding normal (3). Si es tarjeta fina, padding 0 y controlamos dentro. */}

                {loadingStatus ? (
                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
                ) : !showDropzone ? (
                    // --- VISTA COMPACTA (DATOS SINCRONIZADOS) ---
                    <Alert 
                        variant="outlined" 
                        severity="success"
                        icon={<CheckCircleIcon fontSize="inherit" />}
                        action={
                            <Button 
                                color="inherit" 
                                size="small" 
                                startIcon={<EditIcon />}
                                onClick={() => { setShowOverride(true); updateState(initialState); }}
                                sx={{ borderColor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                            >
                                Actualizar Datos
                            </Button>
                        }
                        sx={{ 
                            bgcolor: 'rgba(74, 222, 128, 0.05)', 
                            borderColor: '#4ade80', 
                            color: '#f8fafc',
                            '& .MuiAlert-icon': { color: '#4ade80' },
                            borderRadius: 2,
                            alignItems: 'center',
                            py: 0.5 // Reducimos padding vertical para hacerla "fina"
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Box>
                                <AlertTitle sx={{ fontWeight: 700, color: '#4ade80', mb: 0, fontSize: '0.95rem' }}>
                                    Datos Sincronizados
                                </AlertTitle>
                            </Box>
                            
                            {/* SEPARADOR VERTICAL */}
                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#334155' }} />

                            <Box>
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1 }}>Registros</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{statusData?.total_records?.toLocaleString()}</Typography>
                            </Box>

                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#334155' }} />

                            <Box>
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1 }}>Rango Temporal</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {statusData?.min_date ? new Date(statusData.min_date).toLocaleDateString() : 'N/A'} 
                                    {' - '} 
                                    {statusData?.max_date ? new Date(statusData.max_date).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Box>

                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#334155' }} />

                            <Box>
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1 }}>Última Carga</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {statusData?.last_upload_date ? new Date(statusData.last_upload_date).toLocaleDateString() : '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Alert>
                ) : (
                    // --- VISTA DROPZONE / MAPEO ---
                    <>
                        {/* Título solo si estamos en modo Dropzone */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CloudUploadIcon sx={{ color: '#38bdf8', mr: 1.5 }} />
                            <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
                                {isMapping ? 'Configuración de Columnas' : 'Carga de Facturas'}
                            </Typography>
                            {hasData && (
                                <Button size="small" onClick={() => setShowOverride(false)} sx={{ ml: 'auto', color: '#94a3b8' }}>
                                    Cancelar
                                </Button>
                            )}
                        </Box>

                        {/* ZONA DE CARGA */}
                        {!isMapping && (
                            <Box 
                                component="label" 
                                sx={uploadZoneSx}
                                onDrop={(e) => { e.preventDefault(); if(e.dataTransfer.files?.[0]) { updateState({...initialState, uploading:true}); uploadMutation.mutate(e.dataTransfer.files[0]); } }}
                                onDragOver={(e) => e.preventDefault()}
                            >
                                {state.uploading ? <CircularProgress size={24} sx={{ color: '#38bdf8' }} /> : <AddIcon sx={{ color: '#94a3b8' }} />}
                                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                    {state.uploading ? 'Analizando archivo...' : 'Subir nuevo archivo (Excel/CSV)'}
                                </Typography>
                                <VisuallyHiddenInput type="file" accept=".csv, .xlsx, .xls" onChange={(e) => { if(e.target.files?.[0]) { updateState({...initialState, uploading:true}); uploadMutation.mutate(e.target.files[0]); } }} disabled={state.uploading} />
                            </Box>
                        )}

                        {/* ZONA DE MAPEO */}
                        {isMapping && (
                            <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
                                <Alert severity="info" variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(56, 189, 248, 0.05)', borderColor: '#38bdf8', color: '#bae6fd' }}>
                                    Archivo: <strong>{state.file?.name}</strong>. Asocia tus columnas.
                                </Alert>

                                <TableContainer sx={{ border: '1px solid #334155', borderRadius: 2, mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', width: '30%' }}>CAMPO SISTEMA</TableCell>
                                                <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8' }}>TU ARCHIVO</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {PROCUREMENT_FIELDS.map(field => (
                                                <TableRow key={field.key}>
                                                    <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>
                                                        {field.label} {field.required && <Chip label="Req" size="small" color="error" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />}
                                                        <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>{field.description}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                                        <Select
                                                            fullWidth size="small"
                                                            value={state.mapping[field.key] || ''}
                                                            onChange={(e) => updateState({ mapping: { ...state.mapping, [field.key]: e.target.value } })}
                                                            displayEmpty
                                                            sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#475569' } }}
                                                        >
                                                            <MenuItem value="" disabled><Typography variant="caption">Seleccionar...</Typography></MenuItem>
                                                            {state.userHeaders.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                                                        </Select>
                                                        {state.mapping[field.key] && state.previewData[state.mapping[field.key]] && (
                                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#94a3b8', fontStyle: 'italic' }}>
                                                                Ej: {String(state.previewData[state.mapping[field.key]]).substring(0, 50)}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

                                <Button 
                                    variant="contained" fullWidth size="large"
                                    onClick={() => { updateState({ processing: true, error: null }); processMutation.mutate(); }}
                                    disabled={state.processing}
                                    startIcon={state.processing ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                                    sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, '&:hover': { bgcolor: '#0ea5e9' } }}
                                >
                                    {state.processing ? 'Ingestando...' : 'Confirmar e Ingestar'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* MÓDULO 2: MATRIZ PARETO (INTACTO) */}
            <Paper elevation={0} sx={{ ...cardBaseSx, p: 3, minHeight: '400px' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AutoFixHighIcon sx={{ color: '#facc15' }} />
                            <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600 }}>Matriz de Oportunidad</Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>Top SKUs sin estrategia (Últimos 12 meses).</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Recargar"><IconButton onClick={() => refetchPareto()} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton></Tooltip>
                        <Button variant="contained" disabled={selectedSkus.length === 0} onClick={() => setModalOpen(true)} sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}>
                            Asignar Familia ({selectedSkus.length})
                        </Button>
                    </Stack>
                </Stack>

                {loadingPareto ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#38bdf8' }} /></Box>
                ) : (
                    <TableContainer sx={{ border: '1px solid #334155', borderRadius: 2, maxHeight: '600px' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ bgcolor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                        <Checkbox 
                                            checked={paretoData.length > 0 && selectedSkus.length === paretoData.length}
                                            indeterminate={selectedSkus.length > 0 && selectedSkus.length < paretoData.length}
                                            onChange={(e) => setSelectedSkus(e.target.checked ? paretoData.map(i => i.sku) : [])}
                                            sx={{ color: '#64748b', '&.Mui-checked': { color: '#38bdf8' }, '&.MuiCheckbox-indeterminate': { color: '#38bdf8' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>SKU</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>Frecuencia</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>Precio Medio</TableCell>
                                    <TableCell align="right" sx={{ bgcolor: '#0f172a', color: '#cbd5e1', fontWeight: 700, borderBottom: '1px solid #334155' }}>Gasto Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paretoData.map((row) => (
                                    <TableRow 
                                        key={row.sku} hover selected={selectedSkus.includes(row.sku)}
                                        onClick={() => setSelectedSkus(prev => prev.includes(row.sku) ? prev.filter(s => s !== row.sku) : [...prev, row.sku])}
                                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'rgba(56, 189, 248, 0.08)' }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}
                                    >
                                        <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #1e293b' }}>
                                            <Checkbox checked={selectedSkus.includes(row.sku)} sx={{ color: '#475569', '&.Mui-checked': { color: '#38bdf8' } }} />
                                        </TableCell>
                                        <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #1e293b' }}>{row.sku}</TableCell>
                                        <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #1e293b' }}>{row.frequency}</TableCell>
                                        <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid #1e293b' }}>{row.avg_price.toLocaleString('es-ES', { style: 'currency', currency: row.currency })}</TableCell>
                                        <TableCell align="right" sx={{ color: '#38bdf8', fontWeight: 700, borderBottom: '1px solid #1e293b' }}>{row.total_spend.toLocaleString('es-ES', { style: 'currency', currency: row.currency })}</TableCell>
                                    </TableRow>
                                ))}
                                {paretoData.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8, color: '#64748b' }}><CheckCircleIcon sx={{ fontSize: 40, mb: 1 }} /><Typography>Sin discrepancias pendientes.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* MODAL (INTACTO) */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} PaperProps={{ sx: { bgcolor: '#1e293b', color: '#f8fafc', border: '1px solid #334155', minWidth: '400px' } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #334155' }}>Clasificar SKUs</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <RadioGroup row value={assignMode} onChange={(e) => setAssignMode(e.target.value as any)}>
                        <FormControlLabel value="existing" control={<Radio sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#38bdf8' } }} />} label="Existente" />
                        <FormControlLabel value="new" control={<Radio sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#38bdf8' } }} />} label="Nueva" />
                    </RadioGroup>
                    <Box sx={{ mt: 2 }}>
                        {assignMode === 'existing' ? (
                            <Autocomplete options={families} getOptionLabel={(o) => o.name} onChange={(_, v) => setSelectedFamilyId(v?.id || null)} renderInput={(p) => <TextField {...p} label="Buscar Familia" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#475569' } }, '& .MuiInputLabel-root': { color: '#94a3b8' } }} />} />
                        ) : (
                            <TextField fullWidth label="Nombre Familia" value={newFamilyName} onChange={(e) => setNewFamilyName(e.target.value)} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#475569' } }, '& .MuiInputLabel-root': { color: '#94a3b8' } }} />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #334155' }}>
                    <Button onClick={() => setModalOpen(false)} sx={{ color: '#94a3b8' }}>Cancelar</Button>
                    <Button onClick={() => assignMutation.mutate()} variant="contained" disabled={assignMutation.isPending} sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ProcurementUploadPage;